import _ from 'lodash';
import type { ReactNode } from 'react';

import { HistoryEntryFieldDefault } from './fields/HistoryEntryFieldDefault';
import { entryLayoutStyles } from './styles';

function defaultFormatLabel(key: string) {
	return _.chain(key).kebabCase().split('-').join(' ').capitalize().value();
}

export interface HistoryEntryLayoutProps<S extends object> {
	data: {
		oldData: null | S;
		newData?: S;
	};
	formatValue: (data: S, key: keyof S) => string | null | undefined;
	/**
	 * Return null to use default label
	 */
	formatLabel?: (key: keyof S) => string | null;
	label?: string;

	// If a value needs more complex rendering, then it can be skipped
	// and rendered manually through `children`.

	skipKeys?: (keyof S)[];
	children?: ReactNode;
	keysOrder?: (keyof S)[];
}
export function HistoryEntryLayout<S extends object>({
	data,
	formatValue,
	formatLabel,
	skipKeys,
	children,
	keysOrder,
	label,
}: HistoryEntryLayoutProps<S>) {
	const keys = Object.keys(data.newData ?? {}) as (keyof S)[];

	keys.sort((a, b) => {
		const order = (keysOrder ?? ['id', 'name', 'archived']) as string[];
		const indexA = order.indexOf(String(a));
		const indexB = order.indexOf(String(b));
		if (indexA !== -1 && indexB !== -1) {
			return indexA - indexB;
		} else if (indexA !== -1) {
			return -1;
		} else if (indexB === -1) {
			return String(a).localeCompare(String(b));
		} else {
			return 1;
		}
	});
	return (
		<div css={{ width: '100%' }}>
			{label ? <h3>{label}</h3> : null}
			<div css={entryLayoutStyles.list}>
				{keys.map((key) => {
					if (skipKeys?.includes(key)) {
						return null;
					}
					const oldValue = data.oldData ? formatValue(data.oldData, key) : null;
					const newValue = data.newData ? formatValue(data.newData, key) : null;

					const _label = formatLabel?.(key) ?? defaultFormatLabel(String(key));

					return (
						<HistoryEntryFieldDefault
							key={String(key)}
							label={_label}
							oldValue={oldValue}
							newValue={newValue}
						/>
					);
				})}
				{children}
			</div>
		</div>
	);
}
